
import { useState } from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { UserPlus, RefreshCw, Building } from 'lucide-react';
import { UsersStats } from '@/components/admin/users/UsersStats';
import { UsersSearch } from '@/components/admin/users/UsersSearch';
import { UsersFilter } from '@/components/admin/users/UsersFilter';
import { UsersContent } from '@/components/admin/users/UsersContent';
import { InviteUserDialog } from '@/components/admin/users/InviteUserDialog';
import { useAllOrganizationUsers } from '@/hooks/useAllOrganizationUsers';
import { useUsersFilter } from '@/hooks/useUsersFilter';
import { useNavigate } from 'react-router-dom';

export default function OrganizationUsers() {
  const navigate = useNavigate();
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  
  const {
    users,
    loading,
    refreshing,
    handleUserInvited,
    handleUserUpdated,
    handleManualRefresh
  } = useAllOrganizationUsers();

  const {
    searchQuery,
    setSearchQuery,
    roleFilter,
    setRoleFilter,
    statusFilter,
    setStatusFilter,
    filteredUsers
  } = useUsersFilter(users);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-pulse text-primary">Loading organization users...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Organization Users</h1>
          <p className="text-muted-foreground">
            Manage all organization members across all organizations
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => navigate('/admin/organizations')}
          >
            <Building className="mr-2 h-4 w-4" />
            Organizations
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleManualRefresh}
            disabled={refreshing}
          >
            <RefreshCw className={`mr-2 h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button onClick={() => setInviteDialogOpen(true)}>
            <UserPlus className="mr-2 h-4 w-4" />
            Invite User
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-col space-y-4">
            <UsersStats 
              totalUsers={users.length}
              onManualRefresh={handleManualRefresh}
              refreshing={refreshing}
            />
            <UsersSearch 
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
            />
            <UsersFilter 
              roleFilter={roleFilter}
              statusFilter={statusFilter}
              onRoleFilterChange={setRoleFilter}
              onStatusFilterChange={setStatusFilter}
            />
          </div>
        </CardHeader>
        <CardContent>
          <UsersContent 
            users={users}
            filteredUsers={filteredUsers}
            onUserUpdated={handleUserUpdated}
            onManualRefresh={handleManualRefresh}
            refreshing={refreshing}
          />
        </CardContent>
      </Card>

      <InviteUserDialog
        open={inviteDialogOpen}
        onOpenChange={setInviteDialogOpen}
        onUserInvited={handleUserInvited}
        forPlatformAdmins={false}
      />
    </div>
  );
}
